import { Column, Text } from "@hightouch/htui";
import pluralize from "pluralize";
import { ReferenceLine } from "recharts";

import {
  ChartTooltipSubtitle,
  ChartTooltipTitle,
  ChartTooltipWrapper,
} from "src/components/charts/chart-tooltip";
import { chartStrokeDasharray } from "src/components/charts/chart-utils";
import { CHART_COLORS } from "src/ui/colors";

import type { ChartAnnotation } from "./types";

const MARKER_RADIUS = 4;
const HIT_AREA_WIDTH = 12;
const TOOLTIP_OFFSET_PX = 8;
// Keeps a busy day's tooltip inside a short chart.
const MAX_TOOLTIP_ITEMS = 5;

export type HoveredAnnotation = {
  annotation: ChartAnnotation;
  x: number;
  y: number;
};

type ReferenceLineShapeProps = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
};

// A render function, not a component: recharts before v3 breaks components that wrap its primitives.
export const renderAnnotations = ({
  annotations,
  onHover,
}: {
  annotations: ChartAnnotation[];
  onHover: (hovered: HoveredAnnotation | undefined) => void;
}) =>
  annotations.map((annotation) => (
    <ReferenceLine
      key={annotation.x}
      x={annotation.x}
      // Centered on the bar, since a change lands partway through its bucket.
      position="middle"
      isFront
      shape={({ x1, y1, x2, y2 }: ReferenceLineShapeProps) => {
        const top = Math.min(y1, y2);
        const bottom = Math.max(y1, y2);
        return (
          <g
            style={{ cursor: "default" }}
            onMouseEnter={() => onHover({ annotation, x: x1, y: top })}
            onMouseLeave={() => onHover(undefined)}
          >
            <line
              x1={x1}
              y1={top}
              x2={x2}
              y2={bottom}
              stroke={CHART_COLORS.text}
              strokeDasharray={chartStrokeDasharray}
            />
            <circle
              cx={x1}
              cy={top}
              r={MARKER_RADIUS}
              fill={CHART_COLORS.text}
            />
            <line
              x1={x1}
              y1={top}
              x2={x2}
              y2={bottom}
              stroke="transparent"
              strokeWidth={HIT_AREA_WIDTH}
            />
          </g>
        );
      }}
    />
  ));

export const AnnotationTooltip = ({
  hovered,
  containerWidth,
}: {
  hovered: HoveredAnnotation;
  containerWidth: number;
}) => {
  const opensLeft = hovered.x > containerWidth / 2;
  const hiddenCount = hovered.annotation.items.length - MAX_TOOLTIP_ITEMS;
  return (
    <ChartTooltipWrapper
      position="absolute"
      top={`${hovered.y}px`}
      left={`${hovered.x}px`}
      transform={
        opensLeft
          ? `translateX(calc(-100% - ${TOOLTIP_OFFSET_PX}px))`
          : `translateX(${TOOLTIP_OFFSET_PX}px)`
      }
      maxWidth="sm"
      pointerEvents="none"
      zIndex={1}
    >
      <ChartTooltipTitle>{hovered.annotation.title}</ChartTooltipTitle>
      {hovered.annotation.items.slice(0, MAX_TOOLTIP_ITEMS).map((item) => (
        <Column key={item.key} gap={0.5}>
          <Text color="white">{item.label}</Text>
          {item.description && (
            <ChartTooltipSubtitle>{item.description}</ChartTooltipSubtitle>
          )}
        </Column>
      ))}
      {hiddenCount > 0 && (
        <ChartTooltipSubtitle>
          {hiddenCount} more {pluralize("change", hiddenCount)}
        </ChartTooltipSubtitle>
      )}
    </ChartTooltipWrapper>
  );
};
